import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, CalendarClock, Sparkles, CheckCircle2, ChevronUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@pps/ui/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@pps/ui/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@pps/ui/components/ui/popover';
import { useDailyActivities } from '@/hooks/useDailyActivities';
import { formatCurrency, PRODUCT_TYPES } from '@/lib/constants';
import { cn } from "@pps/ui/lib/utils";

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-destructive/10 text-destructive border-destructive/30',
  medium: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-700',
  low: 'bg-muted text-muted-foreground border-border',
};

export function DailyActivitiesWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { followUps, aiTasks, totalCount, completeFollowUp, completeAiTask } = useDailyActivities();
  const [open, setOpen] = useState(false);

  if (totalCount === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-50 bg-primary hover:bg-primary/90"
        >
          <ClipboardCheck className="h-6 w-6" />
          {totalCount > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1.5 text-[11px] bg-destructive text-destructive-foreground border-0">
              {totalCount > 9 ? '9+' : totalCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] sm:w-96 p-0"
        align="end"
        side="top"
        sideOffset={12}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-card">
          <ClipboardCheck className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-semibold flex-1">{t('dailyActivities.title')}</h4>
          <Badge variant="secondary" className="text-xs">{totalCount}</Badge>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setOpen(false)}>
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>

        <ScrollArea className="max-h-80">
          {/* Follow-ups */}
          {followUps.length > 0 && (
            <div className="px-3 pt-3 pb-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                <CalendarClock className="h-3 w-3" />
                {t('dailyActivities.followUps')} ({followUps.length})
              </p>
              <div className="space-y-1">
                {followUps.map(fu => {
                  const productLabel = PRODUCT_TYPES.find(p => p.value === fu.product_type)?.label || fu.product_type;
                  return (
                    <div key={fu.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/50 transition-colors group">
                      <Checkbox
                        className="shrink-0"
                        onCheckedChange={() => completeFollowUp.mutate(fu.id)}
                      />
                      <button
                        className="flex-1 text-left min-w-0"
                        onClick={() => { setOpen(false); navigate('/oportunidades'); }}
                      >
                        <p className="text-xs font-medium truncate">{fu.lead?.name || '—'}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{productLabel} · {formatCurrency(fu.estimated_value)}</p>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Tasks */}
          {aiTasks.length > 0 && (
            <div className="px-3 pt-3 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                {t('dailyActivities.aiTasks')} ({aiTasks.length})
              </p>
              <div className="space-y-1">
                {aiTasks.map(task => {
                  const priority = (task.metadata?.priority as string) || 'low';
                  return (
                    <div key={task.id} className="flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-muted/50 transition-colors">
                      <Checkbox
                        className="shrink-0 mt-0.5"
                        onCheckedChange={() => completeAiTask.mutate({ activityId: task.id, currentMetadata: task.metadata })}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs leading-snug">{task.description}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Badge variant="outline" className={cn('text-[9px] px-1 py-0 h-3.5', PRIORITY_COLORS[priority])}>
                            {priority}
                          </Badge>
                          {task.lead_name && (
                            <span className="text-[10px] text-muted-foreground truncate">{task.lead_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {totalCount === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mb-2 text-emerald-500" />
              <p className="text-sm font-medium">{t('dailyActivities.allDone')}</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
